/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import _has from "lodash/has";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import formStepsFlexService from "./flexService/formSteps";
import { getProductData } from "../productService";
import _includes from "lodash/includes";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromIban,
  fromNumber,
  fromUserInputtedFloat,
  getConvertedFormDataBase,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizeOrganizationData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toIban,
  toNumber,
  toUserInputtedFloat,
  persistAdditionalClients,
  fromInsurance,
  toInsurance,
} from "../formServiceFunctions";
import ApiClient from "../apiClient";
import _isEmpty from "lodash/isEmpty";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { Customer } from "../../types/Entities/Customer";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (product: AbstractCase): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY]: {
      [product.name]: [],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]: {
      [product.name]: [
        "caseCategory",
        "affectedPartyType",
        "clientWasMinorAtContractDate",
        "contractDate",
        "objectiveClient",
        "subjectOfDispute",
        "clientHasAlreadyClaimed",
        "claimedDateClient",
        "claimedDeadlineClient",
        "claimedAmountClient",
        "overdueFees",
        "defenseProcessStatus",
        "paymentOrderDate",
        "enforcementOrderDate",
        "contractualPartnerHasLawyer",
        "statementOfClaimFiledDate",
        "clientLegalRepresentativeAmount",
        "clientLegalRepresentativesAuthorizationType",
      ],
      opponentLawFirm: ["name", "email", "telephone"],
      "opponentLawFirm.address": ["streetAddress", "postalCode", "addressLocality"],
      opponentLawyer: ["gender", "givenName", "familyName"],
      clientBankData: ["iban"],
      client: ["gender", "givenName", "familyName", "birthDate"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      clientLegalRepresentative: ["gender", "givenName", "familyName"],
      "clientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      secondClientLegalRepresentative: ["gender", "givenName", "familyName"],
      "secondClientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      additionalClients: [],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]: {
      product: ["contractNumber", "buyerPaidStatus", "buyerPaidDate", "buyerPaidAmount"],
      client: ["birthDate"],
      "customer.person": ["birthDate"],
      travelContract: ["reasonForDispute", "bookingDate", "journeyStart", "journeyEnd", "contractualPartnerHasCancelled", "contractualPartnerCancellationDate"],
      purchaseContract: [
        "buyerOrSeller",
        "channel",
        "itemTitle",
        "articleNumber",
        "purchasePrice",
        "goodsOrServiceProvidedStatus",
        "locationOfGood",
        "hasDefects",
        "defectsDescriptionClient",
        "numberRepairAttempts",
        "numberReplacementAttempts",
      ],
      vehicleContract: [
        "buyerOrSeller",
        "vehicleMake",
        "model",
        "vehicleId",
        "purchasePrice",
        "mileageAtPurchase",
        "currentMileage",
        "locationOfGood",
        "hasDefects",
        "isAccidentCar",
        "defectsRecognitionDate",
        "defectsDescriptionClient",
        "goodsOrServiceProvidedStatus",
        "numberRepairAttempts",
        "numberReplacementAttempts",
      ],
      subscriptionContract: ["wasDeceived", "salesChannel", "subscriptionName"],
      membershipContract: [
        "membershipType",
        "reasonForDispute",
        "contractNumber",
        "cancellationDate",
        "endOfContractDate",
        "feePaymentFrequency",
        "fee",
        "numberUnpaidPeriods",
        "priceIncreaseAnnouncementDate",
        "endOfContractDateOpponent",
        "opponentReactionToCancellation",
      ],
      serviceContract: [
        "reasonForDispute",
        "serviceTitle",
        "costEstimate",
        "costEstimateAdjustment",
        "costEstimateAdjustmentDate",
        "costReal",
        "defectsDescriptionClient",
        "numberRepairAttempts",
      ],
      eventContract: ["reasonForDispute", "eventType", "contractNumber", "eventTitle", "eventCity", "eventDate", "numberOfTickets"],
      providerContract: [
        "providerType",
        "reasonForDispute",
        "cancellationReceivedDate",
        "priceIncreaseAnnouncementDate",
        "cancellationDate",
        "endOfContractDate",
        "contractNumber",
        "opponentReactionToCancellation",
        "endOfContractDateOpponent",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]: {
      [product.name]: ["opposingPartyType"],
      opposingPartyLegalEntity: ["legalName", "email", "telephone"],
      "opposingPartyLegalEntity.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      opposingPartyPerson: ["gender", "givenName", "familyName", "email", "telephone"],
      "opposingPartyPerson.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]: {
      [product.name]: ["debtCollectionAgencyReference"],
      debtCollectionAgency: ["name", "email", "telephone"],
      "debtCollectionAgency.address": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]: {
      [product.name]: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]: {
      [product.name]: ["clientHasNoContractDocument"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]: {
      insurance: ["insurance", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "insurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      [product.name]: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "clientBankData.iban",
      convertFrom: fromIban,
      convertTo: toIban,
    },
    {
      path: "contractualPartnerHasLawyer",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "contractDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "client.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "customer.person.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "eventDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "numberOfTickets",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "statementOfClaimFiledDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "costEstimateAdjustmentDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "paymentOrderDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "enforcementOrderDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientHasAlreadyClaimed",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "clientWasMinorAtContractDate",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "wasDeceived",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "claimedDateClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "claimedDeadlineClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "cancellationDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "cancellationReceivedDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "priceIncreaseAnnouncementDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "endOfContractDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "endOfContractDateOpponent",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "claimedAmountClient",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "overdueFees",
      convertFrom: fromFloat,
      convertTo: (value: string | null) => (value === null ? 0 : toFloat(value)),
    },
    {
      path: "insurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "numberRepairAttempts",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "costEstimate",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "costReal",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "fee",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "numberUnpaidPeriods",
      convertFrom: fromUserInputtedFloat,
      convertTo: (value: string | null) => {
        if (!value) {
          return 0;
        }
        return toUserInputtedFloat(value);
      },
    },
    {
      path: "buyerPaidDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "bookingDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "journeyStart",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "journeyEnd",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "contractualPartnerCancellationDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "contractualPartnerHasCancelled",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "buyerPaidAmount",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "purchasePrice",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "mileageAtPurchase",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "currentMileage",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "hasDefects",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "isAccidentCar",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "numberRepairAttempts",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "numberReplacementAttempts",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "defectsRecognitionDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientLegalRepresentativeAmount",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "insurance.insurance",
      convertFrom: fromInsurance,
      convertTo: toInsurance,
    },
  ];

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    createMutation,
    refreshCustomer,
    deleteMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields(product)[applicationStatus] || {};

    let hasCustomerUpdates = false;

    if (_has(fields, "customer.person.residenceAddress") && !!customer.person?.residenceAddress?.id) {
      hasCustomerUpdates = true;
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: customer.person.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      hasCustomerUpdates = true;
      const convertedFormData = this.getConvertedFormData(formData);
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);
      customerPersonData = sanitizePersonData(customerPersonData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: customerPersonData,
      });
    }

    if (_has(fields, "customer")) {
      hasCustomerUpdates = true;
      const customerData = pickAndConvert(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      const baseAddressData = _includes(["customer", "unknown"], formData.affectedPartyType)
        ? formData.customer.person.residenceAddress
        : formData.client.residenceAddress;
      let addressData = pickAndConvert(baseAddressData, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "client")) {
      const convertedFormData = this.getConvertedFormData(formData);
      const baseClientData = _includes(["customer", "unknown"], convertedFormData.affectedPartyType)
        ? convertedFormData.customer.person
        : convertedFormData.client;
      let clientData = pickAndConvert(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "clientLegalRepresentative")) {
      let clientLegalRepresentativeData = pickAndConvert(formData.clientLegalRepresentative, fields.clientLegalRepresentative);
      clientLegalRepresentativeData = sanitizePersonData(clientLegalRepresentativeData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.clientLegalRepresentative.id,
        data: clientLegalRepresentativeData,
      });
    }

    if (_has(fields, "clientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.clientLegalRepresentative.residenceAddress, fields["clientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.clientLegalRepresentative.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "secondClientLegalRepresentative")) {
      let secondClientLegalRepresentativeData = pickAndConvert(formData.secondClientLegalRepresentative, fields.secondClientLegalRepresentative);
      secondClientLegalRepresentativeData = sanitizePersonData(secondClientLegalRepresentativeData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.secondClientLegalRepresentative.id,
        data: secondClientLegalRepresentativeData,
      });
    }

    if (_has(fields, "secondClientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.secondClientLegalRepresentative.residenceAddress, fields["secondClientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.secondClientLegalRepresentative.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "opponentLawFirm")) {
      let opponentLawFirmData = pickAndConvert(formData.opponentLawFirm, fields["opponentLawFirm"]);
      opponentLawFirmData = sanitizeOrganizationData(opponentLawFirmData);
      opponentLawFirmData.name = opponentLawFirmData.name || (formData.opponentLawyer.familyName ? `Kanzlei ${formData.opponentLawyer.familyName}` : "");
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: formData.opponentLawFirm.id,
        data: opponentLawFirmData,
      });
    }

    if (_has(fields, "debtCollectionAgency")) {
      let debtCollectionAgencyData = pickAndConvert(formData.debtCollectionAgency, fields["debtCollectionAgency"]);
      debtCollectionAgencyData = sanitizeOrganizationData(debtCollectionAgencyData);
      if (product.debtCollectionAgency === null) {
        product = await updateMutation.mutateAsync({
          uri: getProductData(product.name, "apiUri"),
          id: product.id,
          data: {
            debtCollectionAgency: "/organizations/new",
          },
        });
      }
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: product.debtCollectionAgency.id,
        data: debtCollectionAgencyData,
      });
      if (_has(fields, "debtCollectionAgency.address")) {
        let debtCollectionAgencyAddressData = pickAndConvert(formData.debtCollectionAgency.address, fields["debtCollectionAgency.address"]);
        debtCollectionAgencyAddressData = sanitizeAddressData(debtCollectionAgencyAddressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.debtCollectionAgency.address.id,
          data: debtCollectionAgencyAddressData,
        });
      }
    }

    if (_has(fields, "clientBankData")) {
      const clientBankData = pickAndConvert(formData.clientBankData, fields["clientBankData"]);
      if (clientBankData.iban) {
        clientBankData.iban = toIban(clientBankData.iban);
      } else {
        delete clientBankData.iban;
      }
      if (!_isEmpty(clientBankData)) {
        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: formData.clientBankData.id,
          data: clientBankData,
        });
      }
    }

    if (_has(fields, "opponentLawFirm.address")) {
      let opponentLawFirmAddressData = pickAndConvert(formData.opponentLawFirm.address, fields["opponentLawFirm.address"]);
      opponentLawFirmAddressData = sanitizeAddressData(opponentLawFirmAddressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: formData.opponentLawFirm.address.id,
        data: opponentLawFirmAddressData,
      });
    }

    if (_has(fields, "opponentLawyer")) {
      let opponentLawyerData = pickAndConvert(formData.opponentLawyer, fields["opponentLawyer"]);
      opponentLawyerData = sanitizePersonData(opponentLawyerData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: formData.opponentLawyer.id,
        data: opponentLawyerData,
      });
    }

    if (formData.opposingPartyType === "opposingPartyLegalEntity") {
      if (_has(fields, "opposingPartyLegalEntity")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyLegalEntity, fields.opposingPartyLegalEntity);
        opposingPartyData = sanitizeOrganizationData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.opposingPartyLegalEntity.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.address")) {
        let addressData = pickAndConvert(formData.opposingPartyLegalEntity.address, fields["opposingPartyLegalEntity.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyLegalEntity.address.id,
          data: addressData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyPerson") {
      if (_has(fields, "opposingPartyPerson")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyPerson, fields.opposingPartyPerson);
        opposingPartyData = sanitizePersonData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.opposingPartyPerson.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyPerson.residenceAddress")) {
        let addressData = pickAndConvert(formData.opposingPartyPerson.residenceAddress, fields["opposingPartyPerson.residenceAddress"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyPerson.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "insurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let insuranceData = pickAndConvert(convertedFormData.insurance, fields["insurance"]);
      await ApiClient.put("/customer_insurances/" + product.insurance.id, { body: JSON.stringify(insuranceData) });
    }

    if (_has(fields, "insurance.policyHolder")) {
      const basePolicyHolderData = formData.insurance.policyHolderType === "me" ? formData.customer.person : formData.insurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["insurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.insurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    if (_has(fields, "additionalClients")) {
      await persistAdditionalClients(formData, product, updateMutation, createMutation, deleteMutation);
    }

    let productData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      productData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, product.name)) {
      productData = _merge(productData, pickAndConvert(formData, fields[product.name]), pickAndConvert(formData, fields.product));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  getConvertedFormData = (data: Partial<AbstractCase>) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({
    howManyClients: product.additionalClients?.length ? "twoClients" : "",
  });

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    if (product.insurance.specialTermsTag === "advocardFlex") {
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);

      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_SUMMARY]);

      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    } else {
      if (formData.applicationStatus <= applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY]);
      }
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
      if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && formData.name !== "otherContract") {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]);
      }
      if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory)) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]);
      }
      if (formData.caseCategory === "defendClaim" && formData.defenseProcessStatus === "debtCollection") {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]);
      }
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
      //for dependencies that are changed in the same step as we are we need to check product instead of formdata to not
      // invalidate our next step since formdata updates before the actual request is fired
      if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && !product.powerOfAttorney) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
      }
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    }

    return availableSteps;
  };
}

export default new FormService();
